import type {
    ClassicalGrid,
    ClassicalShelfItem,
    ClassicalGridComponentItem,
    ClassicalGridItem,
    ClassicalLinkedItem,
} from '@jet-app/music-classical/api/types';
import type {
    ItemKind,
    Section,
} from '@jet-app/music-classical/models/section/Section';
import type { makeIntentBuilderFunction } from '@jet-app/music-classical/intents';
import {
    createFlowAction,
    createFlowActionForItem,
} from '../create-flow-action';
import type { ObjectGraph } from '@jet/environment/dependencies';
import {
    fixArtwork,
    type FlowAction,
    type SectionHeader,
} from '@jet-app/music-classical/models';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import { playActionForItem } from '@jet-app/music-classical/action-model/PlayAction';
import { getId } from './url';
import {
    DETAIL_PAGE_TYPE_ALLOWLIST,
    DetailPageType,
} from '@jet-app/music-classical/intents/detail-page/DetailPageIntent';
import { getSubtitleLink } from './shelf';
import { transformActionUrl } from './url';

export function transformToGridSection(
    component: ClassicalGrid,
    itemKind: ItemKind,
    actionIntentFunction:
        | ReturnType<typeof makeIntentBuilderFunction>
        | undefined,
    objectGraph: ObjectGraph,
    isCharts?: boolean,
    title?: string,
    header?: SectionHeader,
): Section<unknown> {
    return {
        id: component.type || `grid-${itemKind}`,
        sectionKind: 'grid',
        itemKind,
        title,
        header,
        items: component.items?.map((item, index) => ({
            ...getItemAttributesForType(item, objectGraph),
            ...(isCharts ? { ordinal: index + 1 } : {}),
            segue: getSegueForGridItem(item, objectGraph, actionIntentFunction),
        })),
    };
}

function getSegueForGridItem(
    item: ClassicalGridComponentItem,
    objectGraph: ObjectGraph,
    actionIntentFn?: ReturnType<typeof makeIntentBuilderFunction> | undefined,
): FlowAction | void {
    // Multirooms may show up as Browse > Catalog bricks but need to link to a detail page that will be different
    // than any of the other bricks on the page, so we need to special case them as if there is no action intent function
    if (
        !actionIntentFn ||
        (item.action?.hasOwnProperty('screenType') &&
            item.action['screenType'] === 'multiRoom')
    ) {
        return createFlowActionForItem(
            item as unknown as ClassicalShelfItem,
            objectGraph,
            { url: transformActionUrl(item.action?.url) },
        );
    }

    return createFlowAction(
        actionIntentFn,
        getActionParamsForType(item),
        objectGraph,
    );
}

function getItemAttributesForType(
    item: ClassicalGridComponentItem,
    objectGraph: ObjectGraph,
) {
    switch (item.type) {
        case 'linked':
            const linkedItem = item as ClassicalLinkedItem;
            return {
                title: linkedItem?.title,
                subtitle: linkedItem?.subtitle,
                artwork: fixArtwork(linkedItem?.image),
            };
        default:
            const gridItem = item as ClassicalGridItem;
            const attributes: any = {
                id: gridItem.id,
                title: gridItem?.title,
                subtitle: gridItem?.subtitle,
                artwork: gridItem?.image
                    ? fixArtwork(gridItem?.image)
                    : (gridItem as any).artwork, // Fallback to Artwork for Recently Searched Items
                url:
                    transformActionUrl(gridItem.action?.url) ||
                    gridItem?.location,
                type: gridItem?.type,
                playAction: gridItem.playAction ?? playActionForItem(gridItem),
            };

            if (gridItem?.headline) {
                attributes.headline = gridItem?.headline;
            }

            if (gridItem?.contextMenuAction?.url) {
                attributes.contextMenuAction = makeContextualMenuFetchAction(
                    item.contextMenuAction.url,
                );
            }

            if (Array.isArray(gridItem.subtitleActions)) {
                attributes.subtitleLinks = gridItem.subtitleActions
                    .map((action) => getSubtitleLink(action, objectGraph))
                    .filter(Boolean);
            }

            return attributes;
    }
}

function getActionParamsForType(item: ClassicalGridComponentItem) {
    switch (item?.action?.type) {
        case 'searchScreen':
            return {
                term: (item as ClassicalLinkedItem).action?.query,
            };
        default:
            const gridItem = item as ClassicalGridItem;
            return {
                entityType: gridItem?.id,
                entityPath: gridItem?.action?.url,
                url: transformActionUrl(gridItem.action?.url),
                id: gridItem?.id ?? getId(gridItem?.action?.url),
                ...(DETAIL_PAGE_TYPE_ALLOWLIST.includes(
                    gridItem?.action?.screenType as unknown as DetailPageType,
                )
                    ? { pageType: gridItem?.action?.screenType }
                    : {}),
            };
    }
}
